import React, { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import AdminUsers from "@/pages/AdminUsers";
import AdminDatabase from "@/pages/AdminDatabase";
import AdminPianoDefaults from "@/pages/AdminPianoDefaults";
import AdminPlans from "@/pages/AdminPlans";
import AdminGuide from "@/pages/AdminGuide";
import AdminScrMapping from "@/pages/AdminScrMapping";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import {
  Users as UsersIcon, Database, Palette, Settings as SettingsIcon,
  SlidersHorizontal, Check, Loader2, Save, RotateCcw, Calculator, Crown, HelpCircle, FileInput,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  loadLayout, saveLayout, applyLayout, ACCENT_SWATCHES, DENSITY_OPTIONS, DEFAULTS as LAYOUT_DEFAULTS,
} from "@/lib/layoutPrefs";

// Admin-only "Setting" hub. Consolidates User Management, DB Ops, Layout
// customisation and app-wide Parametri into a single tabbed page.
const TABS = [
  { id: "users",    label: "Gestione utenti", icon: UsersIcon },
  { id: "database", label: "Database",        icon: Database },
  { id: "layout",   label: "Layout",          icon: Palette },
  { id: "params",   label: "Parametri",       icon: SlidersHorizontal },
  { id: "piano",    label: "Piano economico", icon: Calculator },
  { id: "plans",    label: "Piani abbonamento", icon: Crown },
  { id: "guide",    label: "Guida utenti",     icon: HelpCircle },
  { id: "scr",      label: "Import SCR",       icon: FileInput },
];

const AdminSettings = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab");
  const active = TABS.find((t) => t.id === rawTab) ? rawTab : "users";

  useEffect(() => {
    if (!TABS.find((t) => t.id === rawTab)) {
      const next = new URLSearchParams(searchParams);
      next.set("tab", "users");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawTab]);

  if (user && user.role !== "admin") return <Navigate to="/boards" replace />;

  const goTab = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", id);
    setSearchParams(next);
  };

  return (
    <div className="min-h-screen">
      <AppHeader />
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-6 flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#12466F] to-[#0A84FF] flex items-center justify-center shadow-md">
            <SettingsIcon className="w-6 h-6 text-white" strokeWidth={1.7} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Amministrazione</div>
            <h1 className="font-display font-bold text-4xl tracking-tighter leading-none mt-1">Setting</h1>
            <p className="text-sm text-slate-500 mt-2 max-w-2xl">
              Gestisci parametri, utenti, limitazioni e configurazioni della piattaforma. Visibile solo agli amministratori.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6" role="tablist" data-testid="settings-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const on = active === t.id;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={on}
                onClick={() => goTab(t.id)}
                data-testid={`settings-tab-${t.id}`}
                className={`flex items-center gap-2 px-4 h-10 rounded-full text-sm font-medium transition-all ${
                  on
                    ? "bg-slate-900 text-white shadow-lg"
                    : "bg-white text-slate-700 border border-slate-200 hover:border-slate-300 hover:shadow-sm"
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.7} />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="ios-card p-5 md:p-6" data-testid={`settings-panel-${active}`}>
          {active === "users" && <AdminUsers embedded />}
          {active === "database" && <AdminDatabase embedded />}
          {active === "layout" && <LayoutPanel />}
          {active === "params" && <ParametersPanel />}
          {active === "piano" && <AdminPianoDefaults />}
          {active === "plans" && <AdminPlans />}
          {active === "guide" && <AdminGuide />}
          {active === "scr" && <AdminScrMapping />}
        </div>
      </div>
    </div>
  );
};

// ------------------ Layout panel (per-user, localStorage) ------------------
const LayoutPanel = () => {
  const [prefs, setPrefs] = useState(() => loadLayout());

  // Live-apply as soon as the user picks something.
  useEffect(() => { applyLayout(prefs); }, [prefs]);

  const change = (patch) => setPrefs((p) => saveLayout({ ...p, ...patch }));
  const reset = () => setPrefs(saveLayout({ ...LAYOUT_DEFAULTS }));

  return (
    <div className="space-y-8" data-testid="layout-panel">
      <div>
        <div className="flex items-center justify-between mb-1">
          <div>
            <h3 className="font-display font-bold text-lg tracking-tight">Colore accent</h3>
            <p className="text-xs text-slate-500 mt-0.5">Determina il colore dei pulsanti primari, badge attivi e evidenziazioni.</p>
          </div>
          <Button variant="ghost" size="sm" className="rounded-full text-slate-500 h-8" onClick={reset} data-testid="layout-reset">
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Ripristina default
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-3" data-testid="accent-swatches">
          {ACCENT_SWATCHES.map((s) => {
            const on = prefs.accent === s.value;
            return (
              <button
                key={s.value}
                type="button"
                onClick={() => change({ accent: s.value })}
                data-testid={`accent-${s.label.toLowerCase()}`}
                title={s.label}
                className={`relative w-10 h-10 rounded-2xl transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2 ${on ? "ring-2 ring-offset-2 ring-slate-900" : ""}`}
                style={{ backgroundColor: s.value }}
              >
                {on && <Check className="w-5 h-5 text-white absolute inset-0 m-auto drop-shadow" strokeWidth={3} />}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <h3 className="font-display font-bold text-lg tracking-tight">Densità tabella immobili</h3>
        <p className="text-xs text-slate-500 mt-0.5">Regola quante righe stanno a schermo senza dover scorrere.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3" data-testid="density-options">
          {DENSITY_OPTIONS.map((d) => {
            const on = prefs.density === d.value;
            return (
              <button
                key={d.value}
                type="button"
                onClick={() => change({ density: d.value })}
                data-testid={`density-${d.value}`}
                className={`text-left rounded-2xl border p-4 transition-all ${on ? "border-[#007AFF] bg-[#007AFF]/5 shadow-sm" : "border-slate-200 hover:border-slate-300"}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-sm text-slate-800">{d.label}</div>
                  {on && <Check className="w-4 h-4 text-[#007AFF]" />}
                </div>
                <div className="text-[11px] text-slate-500 mt-1 leading-snug">{d.desc}</div>
                {/* mini preview */}
                <div className="mt-3 space-y-1" aria-hidden="true">
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="bg-slate-200/70 rounded"
                      style={{ height: d.value === "compact" ? 4 : d.value === "normal" ? 6 : 9 }}
                    />
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <p className="text-[11px] text-slate-500 border-t border-slate-100 pt-4">
        Le preferenze layout sono <b>personali</b> — salvate nel browser (localStorage), sopravvivono al ricaricamento
        della pagina ma non si sincronizzano su altri dispositivi.
      </p>
    </div>
  );
};

// ------------------ Parameters panel (app-wide, backend) ------------------
const ParametersPanel = () => {
  const { refresh: refreshSettings } = useSettings();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/admin/settings"); setForm(r.data); }
      catch { toast.error("Errore caricamento parametri"); }
      finally { setLoading(false); }
    })();
  }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/settings", {
        max_attachment_mb: parseInt(form.max_attachment_mb, 10),
        default_citta: (form.default_citta || "").toUpperCase(),
        scost_band_red: parseFloat(form.scost_band_red),
        scost_band_yellow: parseFloat(form.scost_band_yellow),
        scost_band_green: parseFloat(form.scost_band_green),
        scost_band_cyan: parseFloat(form.scost_band_cyan),
      });
      setForm(r.data);
      refreshSettings();
      toast.success("Parametri salvati");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio parametri");
    } finally { setSaving(false); }
  };

  if (loading || !form) {
    return (
      <div className="flex items-center justify-center py-14 text-slate-500 text-sm" data-testid="params-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento parametri…
      </div>
    );
  }

  const Field = ({ id, label, hint, children }) => (
    <div>
      <Label htmlFor={id} className="text-xs uppercase tracking-wider text-slate-600 font-semibold">{label}</Label>
      {hint && <p className="text-[11px] text-slate-500 mt-0.5 mb-1.5">{hint}</p>}
      {children}
    </div>
  );

  return (
    <div className="space-y-8" data-testid="params-panel">
      <section>
        <h3 className="font-display font-bold text-lg tracking-tight mb-3">Impostazioni generali</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field id="p-max-mb" label="Limite upload allegati (MB per file)" hint="Valori consentiti: 1–32. Impatta il dialog Nuovo/Modifica immobile.">
            <Input
              id="p-max-mb"
              type="number" min={1} max={32}
              value={form.max_attachment_mb}
              onChange={(e) => upd("max_attachment_mb", e.target.value)}
              className="rounded-xl bg-slate-100 border-0 focus-visible:ring-2 focus-visible:ring-[#007AFF] font-mono-num"
              data-testid="param-max-mb"
            />
          </Field>
          <Field id="p-default-citta" label="Città predefinita (nuove schede)" hint="Precompilerà il campo Città nel dialog Nuovo immobile.">
            <Input
              id="p-default-citta"
              value={form.default_citta}
              onChange={(e) => upd("default_citta", e.target.value.toUpperCase())}
              className="rounded-xl bg-slate-100 border-0 focus-visible:ring-2 focus-visible:ring-[#007AFF] uppercase"
              data-testid="param-default-citta"
            />
          </Field>
        </div>
      </section>

      <section>
        <h3 className="font-display font-bold text-lg tracking-tight mb-1">Fasce scostamento (%)</h3>
        <p className="text-xs text-slate-500 mb-3">
          Soglie decimali che determinano il colore del badge scostamento in tabella. Esempio: <b>-30</b> = -30% (occasione forte).
          Ordine consigliato: rosso ≤ giallo ≤ verde ≤ ciano ≤ 0.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { key: "scost_band_red",    label: "Rosso ≤",  color: "#FF3B30" },
            { key: "scost_band_yellow", label: "Giallo ≤", color: "#B27F00" },
            { key: "scost_band_green",  label: "Verde ≤",  color: "#1F7A3A" },
            { key: "scost_band_cyan",   label: "Ciano ≤",  color: "#00A6B7" },
          ].map(({ key, label, color }) => (
            <Field key={key} id={`p-${key}`} label={label}>
              <div className="relative">
                <span
                  aria-hidden="true"
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: color }}
                />
                <Input
                  id={`p-${key}`}
                  type="number" step="0.1"
                  value={form[key]}
                  onChange={(e) => upd(key, e.target.value)}
                  className="rounded-xl bg-slate-100 border-0 pl-8 focus-visible:ring-2 focus-visible:ring-[#007AFF] font-mono-num"
                  data-testid={`param-${key}`}
                />
              </div>
            </Field>
          ))}
        </div>
      </section>

      <section>
        <h3 className="font-display font-bold text-lg tracking-tight mb-1">Quote per piano di abbonamento</h3>
        <p className="text-xs text-slate-500">
          Le quote di <b>immobili</b> e <b>schede</b> per ogni piano si configurano dalla tab <b>Piani abbonamento</b>. Da lì puoi anche riordinare, aggiungere o rimuovere piani.
        </p>
      </section>

      <div className="flex items-center justify-between border-t border-slate-100 pt-4">
        <p className="text-[11px] text-slate-500">
          I parametri sono <b>globali</b>: applicati a tutti gli utenti della piattaforma.
        </p>
        <Button
          onClick={save}
          disabled={saving}
          className="rounded-full h-10 bg-[#007AFF] hover:bg-[#0060df]"
          data-testid="params-save"
        >
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvo…</> : <><Save className="w-4 h-4 mr-2" /> Salva parametri</>}
        </Button>
      </div>
    </div>
  );
};

export default AdminSettings;
